import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import routes from '../../router/config';

/**
 * JBA ヘッダー（2026-07-16 Yuichi設計変更・site-thebutler 同型へ再設計）
 * - 初期(非pinned・ホーム desktop): 看板型2段（上段中央にロゴのロックアップ、下段にメニュー行）
 * - pinned(スクロール後)/内層ページ: 1段コンパクト固定ヘッダー
 * - 状態変化の意味付け(HeaderInner 冒頭コメント準拠): サイズ/レイアウトは不変、
 *   変えるのは背景濃度+影+下端 金ヘアラインの3点のみ（.is-pinned）
 * - モーション定数(headerMotion.ts 移植): exitRatio 0.6 / hysteresis 48px / 350ms ease-out
 * - 質感CSS: src/index.css .jba-* （thebutler catalog-tokens.css 実値移植）
 * - 最上部に全ページ共通 util バー（電話+JP/EN・右端寄せ・thebutler UtilityBar 同型）
 */

// --- 対訳ルート存在チェック用（router/config.tsx を正・創作禁止） ---
const normPath = (p: string) => (p !== '/' ? p.replace(/\/+$/, '') : '/');
const ROUTE_SET = new Set(
  (routes as { path?: string }[])
    .map((r) => r.path || '')
    .filter((p) => p && !p.includes(':') && p !== '*')
    .map((p) => normPath(p))
);
const routeExists = (p: string) => ROUTE_SET.has(normPath(p));

// TJBA 方式: 現在ページの対訳ページへの個別リンク（対訳無い JA 専用は一律 /en＝CEO裁定④）。
function altLangPath(pathname: string, target: 'ja' | 'en'): string {
  const cur = normPath(pathname);
  const isEn = cur === '/en' || cur.startsWith('/en/');
  if (target === 'ja') {
    if (!isEn) return cur;
    return cur === '/en' ? '/' : cur.slice(3) || '/';
  }
  if (isEn) return cur;
  const en = cur === '/' ? '/en' : '/en' + cur;
  return routeExists(en) ? en : '/en';
}

// モーション定数（site-thebutler headerMotion.ts 移植）
const EXIT_RATIO = 0.6;
const HYSTERESIS_PX = 48;
const INNER_THRESHOLD = 80;

interface SubItem { label: string; href: string }
interface NavGroup { title?: string; items: SubItem[] }
interface NavItem {
  key: string;
  label: string;
  href: string;
  head?: SubItem; // ドロップダウン先頭の「一覧」導線
  groups?: NavGroup[];
  align?: 'left' | 'right' | 'center';
  mega?: boolean;
}

export default function Header() {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en');
  const cur = normPath(location.pathname);
  const isHome = cur === '/' || cur === '/en';

  const [pinned, setPinned] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [expandedMobile, setExpandedMobile] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headRef = useRef<HTMLElement>(null);

  // --- スクロール連動 pinned 切替（rAF スロットル + ヒステリシス・thebutler 同型） ---
  useEffect(() => {
    let mounted = true;
    let ticking = false;
    let fixed = false;
    let enter = isHome ? Number.POSITIVE_INFINITY : INNER_THRESHOLD;
    let exit = isHome ? Number.POSITIVE_INFINITY : INNER_THRESHOLD - HYSTERESIS_PX;

    const measure = () => {
      if (!isHome) return;
      const hero = document.querySelector<HTMLElement>('.home-hero');
      const heroH = hero ? hero.getBoundingClientRect().height : 0;
      const headerH = headRef.current ? headRef.current.getBoundingClientRect().height : 0;
      if (heroH > 0) {
        enter = Math.max(0, heroH - headerH);
        exit = enter * EXIT_RATIO;
      }
    };
    const evaluate = () => {
      ticking = false;
      const y = window.scrollY;
      if (!fixed && y >= enter) { fixed = true; setPinned(true); }
      else if (fixed && y <= exit) { fixed = false; setPinned(false); }
    };
    const onScroll = () => { if (ticking) return; ticking = true; window.requestAnimationFrame(evaluate); };
    const remeasure = () => { measure(); evaluate(); };

    measure();
    fixed = window.scrollY >= enter;
    setPinned(fixed);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', remeasure);
    let ro: ResizeObserver | null = null;
    if (isHome && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(remeasure);
      const hero = document.querySelector<HTMLElement>('.home-hero');
      if (hero) ro.observe(hero);
      if (headRef.current) ro.observe(headRef.current);
    }
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => { if (mounted) remeasure(); }).catch(() => {});
    }
    return () => {
      mounted = false;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', remeasure);
      if (ro) ro.disconnect();
    };
  }, [isHome]);

  // Esc でドロップダウンを閉じる
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setActiveMenu(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // 保留中の遅延クローズ用タイマーを unmount 時に解除（未マウントへの setActiveMenu 防止）
  useEffect(() => {
    return () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  }, []);

  const clearCloseTimer = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const openMenu = (key: string) => { clearCloseTimer(); setActiveMenu(key); };
  const scheduleClose = useCallback(() => { clearCloseTimer(); closeTimer.current = setTimeout(() => setActiveMenu(null), 200); }, []);
  const toggleMenu = (key: string) => { clearCloseTimer(); setActiveMenu((c) => (c === key ? null : key)); };

  // --- ナビ構成（router/config.tsx 実測・ラベルは各ページ h1 / Footer 正規名称・創作禁止） ---
  const enHref = (path: string) => (isEnglish ? '/en' + path : path);
  const filt = (items: SubItem[]) => (isEnglish ? items.filter((i) => routeExists(i.href)) : items);

  // ラベルは各ページの実名称/実descriptor（seo-metadata title・h1）由来。日本語で「何のページか」が
  // 一目で分かる表記。英語 slug の直訳/ローマ字放置は禁止。EN は英語ラベル。創作禁止。
  const aboutItems = [
    { label: isEnglish ? 'Overview' : '協会概要', href: enHref('/about/overview') },
    { label: isEnglish ? 'Mission' : '理念と使命', href: enHref('/about/mission') },
    { label: isEnglish ? 'History' : '沿革・歴史', href: enHref('/about/history') },
    { label: isEnglish ? 'Organization' : '組織体制', href: enHref('/about/organization') },
    { label: isEnglish ? 'Activities' : '主な活動', href: enHref('/about/activities') },
    { label: isEnglish ? 'Access' : 'アクセス・所在地', href: enHref('/about/access') },
  ];
  const serviceItems = filt([
    { label: isEnglish ? 'Footman Service' : 'フットマン（基本の執事サービス）', href: enHref('/services/footman') },
    { label: isEnglish ? 'Butler Service' : 'バトラー（上級執事サービス）', href: enHref('/services/butler') },
    { label: isEnglish ? 'Second Steward Service' : 'セカンドスチュワード（最上級サービス）', href: enHref('/services/second-steward') },
    { label: isEnglish ? 'House Steward Service' : 'ハウススチュワード（邸宅執事）', href: enHref('/services/house-steward') },
    { label: isEnglish ? 'What Is a Butler?' : '執事とは', href: enHref('/butler') },
    { label: isEnglish ? 'How to Become a Butler' : '執事になるには', href: enHref('/butler/become') },
  ]);
  const qualBasic = filt([
    { label: isEnglish ? 'Associate Butler' : '初級執事資格（Associate Butler）', href: enHref('/qualifications/associate-butler') },
    { label: isEnglish ? 'Professional Butler' : '中級執事資格（Professional Butler）', href: enHref('/qualifications/professional-butler') },
    { label: isEnglish ? 'Master Butler' : '上級執事資格（Master Butler）', href: enHref('/qualifications/master-butler') },
  ]);
  const qualPro = filt([
    { label: isEnglish ? 'Business Manner' : 'ビジネスマナー', href: enHref('/qualifications/business-manner') },
    { label: isEnglish ? 'Afternoon Tea' : 'アフタヌーンティー', href: enHref('/qualifications/afternoon-tea') },
    { label: isEnglish ? 'Tea Sommelier' : '紅茶・ティーサービス', href: enHref('/qualifications/tea-sommelier') },
    { label: isEnglish ? 'Butler Style & Uniform' : '執事の正装・装い', href: enHref('/qualifications/butler-style-uniform') },
    { label: isEnglish ? 'Wine Steward' : 'ワインサービス', href: enHref('/qualifications/wine-steward') },
    { label: isEnglish ? 'Dining Coordinator' : '食事作法・テーブル演出', href: enHref('/qualifications/dining-coordinator') },
    { label: isEnglish ? 'Event Planning' : '晩餐会・イベント企画', href: enHref('/qualifications/event-planning') },
    { label: isEnglish ? 'Travel Concierge' : '旅行手配・同行', href: enHref('/qualifications/travel-concierge') },
    { label: isEnglish ? 'Household Manager' : '邸宅管理・ハウスキーピング', href: enHref('/qualifications/household-manager') },
  ]);
  const qualLearn = filt([{ label: isEnglish ? 'Distance Learning' : '通信教育プログラム', href: enHref('/qualifications/distance-learning') }]);
  const seminarItems = filt([
    { label: isEnglish ? 'Team Leadership Training' : 'チームリーダーシップ研修', href: enHref('/seminars/corporate/team-leadership') },
    { label: isEnglish ? 'Schedule Management Training' : 'スケジュール管理研修', href: enHref('/seminars/corporate/schedule-management') },
    { label: isEnglish ? 'Cross-Cultural Understanding Training' : '異文化理解研修', href: enHref('/seminars/corporate/cross-cultural-understanding') },
    { label: isEnglish ? 'Hospitality Enhancement Training' : 'ホスピタリティ向上研修', href: enHref('/seminars/corporate/hospitality-enhancement') },
    { label: isEnglish ? 'Communication Skills Training' : 'コミュニケーション技術研修', href: enHref('/seminars/corporate/communication-skills') },
    { label: isEnglish ? 'Crisis Management Training' : '危機管理研修', href: enHref('/seminars/corporate/crisis-management') },
  ]);

  const qualGroups: NavGroup[] = isEnglish
    ? [{ items: qualBasic }]
    : [
        { title: '基本資格', items: qualBasic },
        { title: '専門資格', items: qualPro },
        { title: '学び方', items: qualLearn },
      ];

  // 各ドロップダウンの先頭に「一覧ページ」への導線を置く（Yuichi 2026-07-16）
  const NAV: NavItem[] = [
    { key: 'about', label: isEnglish ? 'About Us' : '協会について', href: enHref('/about'), head: { label: isEnglish ? 'About Overview' : '協会についてトップ', href: enHref('/about') }, groups: [{ items: aboutItems }], align: 'left' },
    { key: 'services', label: isEnglish ? 'Services' : '執事サービス', href: enHref('/butler-services'), head: { label: isEnglish ? 'All Services' : 'サービス一覧', href: enHref('/butler-services') }, groups: [{ items: serviceItems }], align: 'left' },
    { key: 'qualifications', label: isEnglish ? 'Certifications' : '資格制度', href: enHref('/qualifications'), head: { label: isEnglish ? 'All Certifications' : '資格一覧', href: enHref('/qualifications') }, groups: qualGroups, mega: !isEnglish, align: 'center' },
    { key: 'seminars', label: isEnglish ? 'Seminars' : 'セミナー', href: enHref('/seminars'), head: { label: isEnglish ? 'All Seminars' : 'セミナー一覧', href: enHref('/seminars') }, groups: [{ items: seminarItems }], align: 'center' },
    { key: 'news', label: isEnglish ? 'News' : 'ニュース', href: enHref('/news') },
    { key: 'membership', label: isEnglish ? 'Membership' : '会員募集', href: enHref('/membership') },
  ];
  const contactHref = enHref('/contact');
  const contactLabel = isEnglish ? 'Contact' : 'お問い合わせ';
  // ヘッダーCTA = BTN-08 紺背景態のコンパクト版(ヘッダーは紺地: 真鍮塗り+紺枠+紺文字 / hoverで紺塗り+真鍮枠+生成り文字に反転・二重枠インセットは縮小版)。サイズは各出現箇所で付与
  const HEADER_CTA = "relative inline-flex items-center justify-center border border-[#1a2942] bg-[#a38e65] text-white font-semibold transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] before:content-[''] before:absolute before:inset-[3px] before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  // ドロップダウンパネル本体
  const renderPanel = (item: NavItem) => {
    const panelPos = item.align === 'right' ? 'right-0' : item.align === 'center' ? 'left-1/2 -translate-x-1/2' : 'left-0';
    return (
      <div id={`menu-${item.key}`} className={`absolute ${panelPos} top-full pt-2 z-50`}>
        <div className={`jba-dd-panel py-3 ${item.mega ? 'w-[560px] px-6' : 'min-w-[240px]'}`}>
          {item.head && (
            <>
              <a href={item.head.href} className={`jba-dd-link flex items-center gap-1 ${item.mega ? 'px-2' : 'px-5'} py-2 text-[13px] tracking-[0.06em] text-[#a38e65] font-medium`}>
                {item.head.label}<i className="ri-arrow-right-s-line text-white/50"></i>
              </a>
              <div className={`${item.mega ? 'px-2' : 'px-5'} pb-1 mb-1`}><span className="block h-px bg-white/10"></span></div>
            </>
          )}
          {item.mega ? (
            <div className="grid grid-cols-2 gap-x-8 gap-y-1">
              {item.groups!.map((g, gi) => (
                <div key={gi} className={gi === item.groups!.length - 1 ? 'col-span-2 pt-2 mt-1 border-t border-white/10' : ''}>
                  {g.title && <div className="text-[12px] tracking-wider text-white/45 px-2 pb-1">{g.title}</div>}
                  <ul>
                    {g.items.map((s) => (
                      <li key={s.href}>
                        <a href={s.href} className="jba-dd-link block px-2 py-2 text-[15px] tracking-[0.04em] text-white/85">{s.label}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <ul>
              {item.groups!.map((g) => g.items.map((s) => (
                <li key={s.href}>
                  <a href={s.href} className="jba-dd-link block px-5 py-2.5 text-[15px] tracking-[0.04em] text-white/85 whitespace-nowrap">{s.label}</a>
                </li>
              )))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  // 1つのナビ項目（親リンク + chevron ボタン + パネル）
  const renderNavItem = (item: NavItem, big: boolean) => {
    const hasMenu = !!item.groups && item.groups.some((g) => g.items.length > 0);
    const linkCls = big
      ? "jba-navlink font-['Noto_Sans_JP',sans-serif] text-white/85 text-[14px] tracking-[0.1em] px-3.5 py-2.5"
      : "jba-navlink font-['Noto_Sans_JP',sans-serif] text-white/85 text-[13.5px] tracking-[0.08em] px-3 py-2";
    if (!hasMenu) {
      return <a key={item.key} href={item.href} className={linkCls}>{item.label}</a>;
    }
    const open = activeMenu === item.key;
    return (
      <div
        key={item.key}
        className="relative flex items-center"
        onMouseEnter={() => openMenu(item.key)}
        onMouseLeave={scheduleClose}
        onFocus={() => openMenu(item.key)}
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) scheduleClose(); }}
      >
        <a href={item.href} className={linkCls}>{item.label}</a>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={`menu-${item.key}`}
          aria-label={`${item.label} submenu`}
          onClick={() => toggleMenu(item.key)}
          className="-ml-2 p-1 text-white/60 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#a38e65] rounded"
        >
          <i className={`ri-arrow-down-s-line text-[15px] transition-transform duration-300 ${open ? 'rotate-180' : ''}`}></i>
        </button>
        {open && renderPanel(item)}
      </div>
    );
  };

  // util バー（電話 + JP/EN・右端寄せ・thebutler UtilityBar 同型）
  const utilBar = (
    <div className="bg-[#16223b] border-b border-white/10">
      <div className="max-w-[1600px] mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-end gap-[18px] h-10 md:h-9">
          <a href="tel:+81-3-6824-4086" aria-label={isEnglish ? 'Call 03-6824-4086' : '電話 03-6824-4086'} className="jba-util-tel inline-flex items-center gap-[7px] text-[15px] md:text-[16px] text-white/85 hover:text-[#d4af37] transition-colors duration-300">
            <i className="ri-phone-line text-[13px] text-[#a38e65]"></i>
            <span>03-6824-4086</span>
          </a>
          <span className="w-px h-[13px] bg-white/20" aria-hidden="true"></span>
          <div className="flex items-center gap-[9px] font-['Noto_Sans_JP',sans-serif] text-[14px] font-medium tracking-[0.1em]" role="group" aria-label={isEnglish ? 'Language' : '言語切替'}>
            <a href={altLangPath(location.pathname, 'ja')} aria-current={!isEnglish ? 'true' : undefined} className={!isEnglish ? 'text-[#d4af37]' : 'text-white/55 hover:text-white transition-colors duration-300'}>JP</a>
            <span className="w-px h-[10px] bg-white/20" aria-hidden="true"></span>
            <a href={altLangPath(location.pathname, 'en')} aria-current={isEnglish ? 'true' : undefined} className={isEnglish ? 'text-[#d4af37]' : 'text-white/55 hover:text-white transition-colors duration-300'}>EN</a>
          </div>
        </div>
      </div>
    </div>
  );

  const showSignboard = isHome && !pinned; // desktop 看板2段（CSS で lg 以上のみ表示）

  return (
    <header ref={headRef} className="fixed top-0 left-0 right-0 z-50 text-white">
      {utilBar}

      {/* ===== デスクトップ ===== */}
      <div className="hidden lg:block">
        {showSignboard ? (
          // 看板型2段（トップ位置・ホーム）
          <div className="bg-[#1a2942] shadow-2xl">
            <div className="max-w-[1600px] mx-auto px-6 sm:px-8 lg:px-12">
              {/* 上段: ロゴのロックアップ（中央） */}
              <div className="flex flex-col items-center gap-2 pt-7 pb-5">
                <a href={isEnglish ? '/en' : '/'} className="flex flex-col items-center gap-2.5 group transition-opacity duration-300 hover:opacity-90">
                  <img src="/images/brand/jba-logo.png" alt="一般社団法人 全日本執事協会 紋章" className="w-[76px] h-[76px] object-contain opacity-95" />
                  {isEnglish ? (
                    <>
                      <span className="jba-signboard-assoc text-[12px] tracking-[0.34em] text-[#d4c49a]/80 pl-[0.34em]">The All Japan Butler Association</span>
                      <span className="jba-signboard-logo text-[clamp(28px,3.2vw,42px)] text-[#d4c49a] pl-[0.30em]">JAPAN&nbsp;<b>BUTLER</b>&nbsp;ASSOCIATION</span>
                    </>
                  ) : (
                    <>
                      {/* JA=classic 組み: 英字小(上)+ 日本語主(下・最大)。配色はシャンパンゴールドで統一 */}
                      <span className="jba-signboard-logo text-[12px] tracking-[0.42em] text-[#d4c49a]/80 pl-[0.42em]">JAPAN BUTLER ASSOCIATION</span>
                      <span className="jba-display font-medium text-[clamp(24px,3vw,36px)] tracking-[0.12em] text-[#d4c49a] pl-[0.12em] whitespace-nowrap">
                        <span className="text-[0.55em] tracking-[0.08em] text-[#d4c49a]/80 mr-[0.5em] align-middle">一般社団法人</span>全日本執事協会
                      </span>
                    </>
                  )}
                </a>
              </div>
              {/* 下段: メニュー行（中央） */}
              <nav className="jba-signboard-nav flex items-center justify-center flex-wrap gap-[clamp(2px,0.5vw,10px)] py-3 border-t border-white/[0.14]" aria-label={isEnglish ? 'Main navigation' : 'メインナビゲーション'}>
                {NAV.map((item) => renderNavItem(item, true))}
                <a href={contactHref} className={`${HEADER_CTA} ml-2.5 px-6 py-[11px] text-[12.5px] font-semibold font-['Noto_Sans_JP',sans-serif] uppercase`}>{contactLabel}</a>
              </nav>
            </div>
          </div>
        ) : (
          // 1段コンパクト（pinned / 内層）
          <div className={`jba-hd-bar bg-[#1a2942] ${pinned ? 'is-pinned' : ''}`}>
            <div className="max-w-[1600px] mx-auto pr-6 pl-4 sm:pr-8 sm:pl-5 lg:pr-12 lg:pl-6">
              <div className="flex items-center justify-between gap-5 h-16">
                <a href={isEnglish ? '/en' : '/'} className="flex items-center gap-3 shrink-0 transition-opacity duration-300 hover:opacity-90">
                  <img src="/images/brand/jba-logo.png" alt="一般社団法人 全日本執事協会 紋章" className="w-9 h-9 object-contain shrink-0" />
                  {isEnglish ? (
                    <span className="jba-signboard-logo text-[19px] tracking-[0.2em] text-[#d4c49a]">JAPAN&nbsp;<b>BUTLER</b>&nbsp;ASSOCIATION</span>
                  ) : (
                    // pinned 左ロゴ=横並び: 日本語主 + 右に英字小1行（縦積み禁止・区切りは細ヘアライン）
                    <span className="flex items-center gap-2.5">
                      <span className="jba-display font-semibold text-[19px] tracking-[0.1em] text-[#d4c49a] whitespace-nowrap"><span className="text-[0.55em] tracking-[0.08em] text-[#d4c49a]/80 mr-[0.4em] align-middle">一般社団法人</span>全日本執事協会</span>
                      <span className="w-px h-4 bg-[#d4c49a]/30" aria-hidden="true"></span>
                      <span className="jba-signboard-logo text-[9px] tracking-[0.28em] text-[#d4c49a]/75 whitespace-nowrap">JAPAN BUTLER ASSOCIATION</span>
                    </span>
                  )}
                </a>
                <nav className="flex items-center flex-wrap gap-[clamp(2px,0.4vw,8px)]" aria-label={isEnglish ? 'Main navigation' : 'メインナビゲーション'}>
                  {NAV.map((item) => renderNavItem(item, false))}
                  <a href={contactHref} className={`${HEADER_CTA} ml-2 px-5 py-2.5 text-[12px] font-semibold font-['Noto_Sans_JP',sans-serif] uppercase`}>{contactLabel}</a>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===== モバイル（常にコンパクト + バーガー） ===== */}
      <div className={`lg:hidden jba-hd-bar bg-[#1a2942] ${pinned || isMenuOpen ? 'is-pinned' : ''}`}>
        <div className="max-w-[1600px] mx-auto px-6 sm:px-8">
          <div className="flex items-center justify-between h-14">
            <a href={isEnglish ? '/en' : '/'} className="flex items-center gap-2.5 shrink-0">
              <img src="/images/brand/jba-logo.png" alt="一般社団法人 全日本執事協会 紋章" className="w-8 h-8 object-contain shrink-0" />
              {isEnglish ? (
                <span className="jba-signboard-logo text-[13px] tracking-[0.1em] text-[#d4c49a]">JAPAN&nbsp;<b>BUTLER</b>&nbsp;ASSOCIATION</span>
              ) : (
                <span className="jba-display font-semibold text-[15px] tracking-[0.08em] text-[#d4c49a] whitespace-nowrap"><span className="text-[0.55em] tracking-[0.08em] text-[#d4c49a]/80 mr-[0.35em] align-middle">一般社団法人</span>全日本執事協会</span>
              )}
            </a>
            <button type="button" aria-expanded={isMenuOpen} aria-label={isMenuOpen ? (isEnglish ? 'Close menu' : 'メニューを閉じる') : (isEnglish ? 'Open menu' : 'メニューを開く')} onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-white/85 hover:text-[#d4af37] transition-colors duration-300">
              <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="bg-[#16223b] border-t border-[#a38e65]/40 max-h-[70vh] overflow-y-auto">
            <div className="px-2 py-3 space-y-1">
              {NAV.map((item) => {
                const hasMenu = !!item.groups && item.groups.some((g) => g.items.length > 0);
                if (!hasMenu) {
                  return (
                    <Link key={item.key} to={item.href} className="block text-center text-white/90 hover:text-[#d4af37] transition-colors duration-300 font-['Noto_Serif_JP',serif] px-3 py-3" onClick={() => setIsMenuOpen(false)}>{item.label}</Link>
                  );
                }
                const exp = expandedMobile === item.key;
                const subId = `m-${item.key}`;
                return (
                  <div key={item.key} className="border-b border-white/5 last:border-0">
                    <div className="flex items-center">
                      {/* 中央表示のため右のチェブロン分を左側でバランス */}
                      <span className="w-10 shrink-0" aria-hidden="true"></span>
                      <Link to={item.href} className="flex-1 text-center text-white/90 hover:text-[#d4af37] transition-colors duration-300 font-['Noto_Serif_JP',serif] px-3 py-3" onClick={() => setIsMenuOpen(false)}>{item.label}</Link>
                      <button type="button" aria-expanded={exp} aria-controls={subId} aria-label={`${item.label} submenu`} onClick={() => setExpandedMobile(exp ? null : item.key)} className="w-10 shrink-0 py-3 text-white/60 hover:text-white">
                        <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${exp ? 'rotate-180' : ''}`}></i>
                      </button>
                    </div>
                    <div id={subId} className={`overflow-hidden transition-all duration-300 ${exp ? 'max-h-[70vh] overflow-y-auto' : 'max-h-0'}`}>
                      <div className="pb-2 text-center">
                        {item.head && (
                          <a href={item.head.href} className="block text-center text-[#a38e65] hover:text-[#d4af37] transition-colors duration-200 font-['Noto_Serif_JP',serif] px-3 py-2 text-sm font-medium" onClick={() => setIsMenuOpen(false)}>{item.head.label}</a>
                        )}
                        {item.groups!.map((g, gi) => (
                          <div key={gi}>
                            {g.title && <div className="text-[12px] tracking-wider text-white/45 text-center px-3 pt-2 pb-1">{g.title}</div>}
                            {g.items.map((s) => (
                              <a key={s.href} href={s.href} className="block text-center text-white/80 hover:text-[#d4af37] transition-colors duration-200 font-['Noto_Serif_JP',serif] px-3 py-2 text-sm" onClick={() => setIsMenuOpen(false)}>{s.label}</a>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-center mx-3 my-2">
                <a href={contactHref} className={`${HEADER_CTA} px-4 py-3 text-[13px] font-semibold uppercase text-center`} onClick={() => setIsMenuOpen(false)}>{contactLabel}</a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
